import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface UserData {
  id: string;
  email: string;
  roles: string[];
}

interface UserRolesManagerProps {
  user: UserData;
  onRolesUpdated: () => void;
}

const AVAILABLE_ROLES: Array<'registered' | 'subscriber' | 'admin'> = ['registered', 'subscriber', 'admin'];
const SUPER_ADMIN_EMAIL = 'gabrielelakhal@gmail.com';

export default function UserRolesManager({ user, onRolesUpdated }: UserRolesManagerProps) {
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const isSuperAdmin = user.email === SUPER_ADMIN_EMAIL;
  const [selectedRoles, setSelectedRoles] = useState<string[]>(user.roles);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sincronizza lo stato locale quando i ruoli dell'utente cambiano
  useEffect(() => {
    setSelectedRoles(user.roles);
  }, [user.roles]);

  const handleRoleToggle = async (role: string, checked: boolean) => {
    setIsUpdating(true);

    try {
      const { data, error } = await supabase.functions.invoke('manage-user-role', {
        body: {
          targetUserId: user.id,
          role: role,
          action: checked ? 'add' : 'remove'
        }
      });

      // Handle FunctionsHttpError - parse response body for error message
      if (error) {
        let errorMessage = '';
        try {
          const errorContext = (error as any).context;
          if (errorContext?.body) {
            const parsed = JSON.parse(errorContext.body);
            errorMessage = parsed.error || '';
          }
        } catch {
          // If parsing fails, use generic error
        }
        
        if (errorMessage.toLowerCase().includes('last admin')) {
          toast({ 
            title: t("admin.users.detail.rolesManager.toast.errorLastAdmin"),
            description: t("admin.users.detail.rolesManager.toast.errorLastAdminDesc"),
            variant: "error" 
          });
        } else if (errorMessage) {
          toast({ title: errorMessage, variant: "error" });
        } else {
          toast({ title: t("admin.users.detail.rolesManager.toast.errorUpdating"), variant: "error" });
        }
        return;
      }

      if (data?.error) {
        if (data.error.toLowerCase().includes('last admin')) {
          toast({ 
            title: t("admin.users.detail.rolesManager.toast.errorLastAdmin"),
            description: t("admin.users.detail.rolesManager.toast.errorLastAdminDesc"),
            variant: "error" 
          });
        } else {
          toast({ title: data.error, variant: "error" });
        }
        return;
      }

      // Update local state with roles from backend
      console.debug('Ruoli aggiornati dal backend:', data.roles);
      setSelectedRoles(data.roles || []);
      
      toast({ 
        title: checked 
          ? t("admin.users.detail.rolesManager.toast.roleAdded", { role })
          : t("admin.users.detail.rolesManager.toast.roleRemoved", { role }), 
        variant: "success" 
      });

      onRolesUpdated();
    } catch (error) {
      console.error('Error updating role:', error);
      toast({ title: t("admin.users.detail.rolesManager.toast.errorUpdating"), variant: "error" });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-4">
      {isSuperAdmin && (
        <div className="px-3 py-2 bg-primary/10 border-2 border-primary text-sm font-semibold rounded-lg">
          🛡️ Super Amministratore
        </div>
      )}
      
      <p className="text-sm text-muted-foreground">
        {t("admin.users.detail.rolesManager.description")}
      </p>

      <div className="space-y-3">
        {AVAILABLE_ROLES.map((role) => (
          <div key={role} className="flex items-center space-x-2">
            <Checkbox
              id={`role-${role}`}
              checked={selectedRoles.includes(role)}
              onCheckedChange={(checked) => handleRoleToggle(role, checked as boolean)}
              disabled={isUpdating}
            />
            <label
              htmlFor={`role-${role}`}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t(`admin.users.detail.rolesManager.roles.${role}`)}
            </label>
          </div>
        ))}
      </div>

      {isUpdating && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("admin.users.detail.rolesManager.updating")}
        </div>
      )}
    </div>
  );
}